import type { ReactNode } from 'react';
import { useT } from '../../i18n';
import { apiBase } from '../../native/platform';
import { useSession } from '../../store/session';
import { apkUpdate } from '../../update/decide';
import { useInstall } from '../../update/install';
import { useUpdate } from '../../update/store';
import { Cross } from '../icons';
import styles from './UpdateBanner.module.css';

/** One slim bar under the top bar: a web build waiting for a reload, or a newer APK to install. */
export function UpdateBanner() {
  const t = useT();
  const webReady = useUpdate((s) => s.webReady);
  const dismissedWeb = useUpdate((s) => s.dismissedWeb);
  const reloadWeb = useUpdate((s) => s.reloadWeb);
  const dismissWeb = useUpdate((s) => s.dismissWeb);
  const installed = useUpdate((s) => s.installed);
  const dismissedApk = useUpdate((s) => s.dismissedApk);
  const dismissApk = useUpdate((s) => s.dismissApk);
  const apk = useSession((s) => s.meta?.apk ?? null);
  const serverVersion = useSession((s) => s.meta?.version ?? null);
  const serverBuild = useSession((s) => s.meta?.buildId ?? null);
  const phase = useInstall((s) => s.phase);
  const progress = useInstall((s) => s.progress);
  const start = useInstall((s) => s.start);
  const install = useInstall((s) => s.install);

  if (webReady && !dismissedWeb) {
    return (
      <Bar
        text={t.t('update.web', {
          current: `${__APP_VERSION__} (${__BUILD_ID__})`,
          next: serverBuild && serverBuild !== __BUILD_ID__ ? `${serverVersion} (${serverBuild})` : '…',
        })}
        dismissLabel={t.t('update.dismiss')}
        onDismiss={dismissWeb}
      >
        <button type="button" className={styles.action} onClick={() => void reloadWeb?.()}>
          {t.t('update.reload')}
        </button>
      </Bar>
    );
  }
  const offer = apkUpdate(installed, apk, dismissedApk);
  if (offer && offer.versionCode !== null) {
    const code = offer.versionCode;
    const busy = phase === 'downloading' || phase === 'installing';
    const text =
      phase === 'downloading' ? t.t('update.downloading', { pct: Math.round(progress * 100) })
      : phase === 'installing' ? t.t('update.installing')
      : phase === 'downloaded' ? t.t('update.downloaded')
      : phase === 'permission' ? t.t('update.allowInstall')
      : phase === 'error' ? t.t('update.failed')
      : t.t('update.apk', { current: installed ? `${installed.versionName} (${installed.versionCode})` : '?', version: offer.versionName ?? '?', code });
    return (
      <Bar text={text} dismissLabel={t.t('update.dismiss')} onDismiss={() => dismissApk(code)}>
        {phase === 'downloaded' ? (
          <button type="button" className={styles.action} onClick={() => void install()}>
            {t.t('update.install')}
          </button>
        ) : (
          <button type="button" className={styles.action} disabled={busy} onClick={() => void start(`${apiBase()}/downloads/hmawiw.apk`)}>
            {t.t('update.get')}
          </button>
        )}
      </Bar>
    );
  }
  return null;
}

function Bar({ text, dismissLabel, onDismiss, children }: { text: string; dismissLabel: string; onDismiss: () => void; children: ReactNode }) {
  return (
    <div className={styles.bar} role="status">
      <span className={styles.text}>{text}</span>
      {children}
      <button type="button" className={styles.close} onClick={onDismiss} aria-label={dismissLabel} title={dismissLabel}>
        <Cross size={18} />
      </button>
    </div>
  );
}
